import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { toast } from 'react-toastify';

import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

import { ToastConfig } from './Toastify.config';

const isAdminRoute = () => {
  return window.location.pathname.startsWith('/admin');
};

const isCertificationRoute = () => {
  return window.location.pathname.startsWith('/certification');
};

export const useGetQueryClient = (): QueryClient => {
  const { t } = useTranslationWithPrefix('utils.toasts.fetch');

  const handleMinorError = () => {
    if (isAdminRoute()) return;
    toast(t('error'), ToastConfig.error);
  };

  const handleOperationSuccess = () => {
    if (isAdminRoute() || isCertificationRoute()) return;
    toast(t('success'), ToastConfig.success);
  };

  return useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        mutations: {
          retry: false,
        },
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          if (mutation?.options?.meta?.disableErrorAlert) {
            return;
          }
          handleError(error, handleMinorError);
        },
        onSuccess: (_data, _variables, _context, mutation) => {
          if (mutation?.options?.meta?.disableSuccessAlert) {
            return;
          }
          handleOperationSuccess();
        },
      }),
      queryCache: new QueryCache({
        onError: (error: unknown, query) => {
          if (!query?.options?.meta?.disableErrorAlert) {
            handleError(error, handleMinorError);
          }
        },
        onSuccess: (_data, query) => {
          if (query?.options?.meta?.enableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
    });
  }, []);
};

const handleError = (error: unknown, handleMinorError: (error: string) => void) => {
  if (!error) {
    handleMinorError('Unknown Error');
    return;
  }
  if (typeof error === 'string') {
    handleMinorError(error);
    return;
  }

  // Check if this is a 403 with internal-error 4003 (insufficient permissions)
  // In this case, we don't want to show a toast because a modal is already shown
  const axiosError = error as AxiosError;
  const internalError = (axiosError?.response?.data as { 'internal-error'?: number | string })?.[
    'internal-error'
  ];

  if (axiosError?.response?.status === 403 && String(internalError) === '4003') {
    // Don't show toast for permission errors - modal is already shown
    return;
  }

  handleMinorError(axiosError?.message);
};
